import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject, debounceTime, finalize} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import Swiper from 'swiper';
import {Autoplay} from 'swiper/modules';
import {BusinessDirectoryService} from '../api/business-directory.service';
import {ChapterApiService} from '../api/chapter-api.service';
import {BusinessDirectory, BusinessSearchParams} from '../models/business-directory';
import {BUSINESS_CATEGORIES_GROUPED, STATES} from '../constants';
import {BIZ_SIZES, LOOKING_FOR, categoryName, groupCodes, initialsOf} from './biz-categories';

/**
 * The public business directory: search and filters, a monthly spotlight, a "newly added" carousel and a card grid
 * or table. Anyone can browse; only members can list a business (BUSINESS:CREATE).
 */
@Component({
    selector: 'app-biz-directory',
    templateUrl: './biz-directory.component.html',
    styleUrls: ['./biz-directory.component.css']
})
export class BizDirectoryComponent implements OnInit, OnDestroy {
    readonly pageSize = 12;
    groups = BUSINESS_CATEGORIES_GROUPED;
    states = STATES;
    lookingForOptions = LOOKING_FOR;
    sizes = BIZ_SIZES;
    category = categoryName;
    initials = initialsOf;

    userId = sessionStorage.getItem('userId') || '';
    loggedIn = sessionStorage.getItem('username') !== null;
    canAdd = false;

    // filters
    q = '';
    group = '';
    industry = '';
    state = '';
    chapterId = '';
    lookingFor = '';
    mentorOnly = false;
    verifiedOnly = false;
    mineOnly = false;
    followingOnly = false;
    digestSubscribed = false;
    sort: 'newest' | 'name' | 'popular' = 'newest';
    view: 'cards' | 'table' = 'cards';

    chapters: { id: string; name: string }[] = [];
    chapterNames = new Map<string, string>();

    results: BusinessDirectory[] = [];
    total = 0;
    page = 1;          // ngx-pagination is 1-based; the API is 0-based
    loading = false;
    loaded = false;

    spotlight: BusinessDirectory | null = null;
    newest: BusinessDirectory[] = [];

    private search$ = new Subject<void>();
    private swiper: Swiper | null = null;

    constructor(
        private bizService: BusinessDirectoryService,
        private chapterApi: ChapterApiService,
        private toastr: ToastrService,
        private router: Router,
        private route: ActivatedRoute
    ) {
    }

    // The carousel only renders once "newly added" has loaded, so Swiper is created when its element appears.
    @ViewChild('carousel') set carousel(el: ElementRef<HTMLElement> | undefined) {
        if (el && !this.swiper) {
            this.swiper = new Swiper(el.nativeElement, {
                modules: [Autoplay],
                slidesPerView: 1,
                speed: 500,
                rewind: true,
                // the page can render while its panel is still collapsed or animating open; re-measure once it is visible
                observer: true,
                observeParents: true,
                autoplay: {delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true},
                breakpoints: {768: {slidesPerView: 2}, 1200: {slidesPerView: 3}}
            });
        } else if (!el && this.swiper) {
            this.swiper.destroy();
            this.swiper = null;
        }
    }

    ngOnInit(): void {
        const perms = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.canAdd = !!perms?.BUSINESS?.includes('CREATE');

        const query = this.route.snapshot.queryParamMap;
        this.mentorOnly = query.get('mentor') === '1';
        this.q = query.get('q') || '';

        this.search$.pipe(debounceTime(300)).subscribe(() => this.runSearch());
        this.chapterApi.getData().subscribe({
            next: (list: any[]) => {
                this.chapters = (list || []).map(c => ({id: c.id, name: c.name}));
                this.chapters.forEach(c => this.chapterNames.set(c.id, c.name));
            },
            error: err => console.error('Failed to load chapters', err)
        });
        this.bizService.getSpotlight().subscribe({
            next: s => this.spotlight = s,
            error: err => console.error('Failed to load spotlight', err)
        });
        this.bizService.search({sort: 'newest', size: 9}).subscribe({
            next: p => {
                this.newest = p.items;
                setTimeout(() => this.swiper?.update(), 0);
            },
            error: err => console.error('Failed to load newest businesses', err)
        });
        this.runSearch();
        if (this.loggedIn) {
            this.bizService.isSubscribed().subscribe({
                next: r => this.digestSubscribed = r.subscribed,
                error: err => console.error('Failed to read digest subscription', err)
            });
        }
    }

    toggleDigest(): void {
        const request = this.digestSubscribed ? this.bizService.unsubscribe() : this.bizService.subscribe();
        request.subscribe({
            next: () => {
                this.digestSubscribed = !this.digestSubscribed;
                this.toastr.success(this.digestSubscribed ? 'You will get the weekly directory email' : 'You are unsubscribed from the weekly email');
            },
            error: err => {
                console.error('Digest subscription failed', err);
                this.toastr.error('Could not update your subscription');
            }
        });
    }

    ngOnDestroy(): void {
        this.swiper?.destroy();
        this.search$.complete();
    }

    get filtersActive(): boolean {
        return !!(this.q || this.group || this.industry || this.state || this.chapterId || this.lookingFor ||
            this.mentorOnly || this.verifiedOnly || this.mineOnly || this.followingOnly);
    }

    get groupCategories() {
        return this.groups.find(g => g.group === this.group)?.categories || [];
    }

    location(biz: BusinessDirectory): string {
        return [biz.city, biz.state].filter(Boolean).join(', ');
    }

    chapterName(biz: BusinessDirectory): string {
        return biz.ownerChapterId ? (this.chapterNames.get(biz.ownerChapterId) || '') : '';
    }

    /** Typing waits for a pause; every other filter searches straight away. */
    onTyping(): void {
        this.page = 1;
        this.search$.next();
    }

    onFilterChange(): void {
        this.page = 1;
        this.runSearch();
    }

    pickGroup(name: string): void {
        this.group = this.group === name ? '' : name;
        this.industry = '';
        this.onFilterChange();
    }

    clearFilters(): void {
        this.q = this.group = this.industry = this.state = this.chapterId = this.lookingFor = '';
        this.mentorOnly = this.verifiedOnly = this.mineOnly = this.followingOnly = false;
        this.onFilterChange();
    }

    onPageChange(p: number): void {
        this.page = p;
        this.runSearch();
        window.scrollTo({top: 0, behavior: 'smooth'});
    }

    private params(): BusinessSearchParams {
        const industries = this.industry ? [this.industry] : (this.group ? groupCodes(this.group) : []);
        return {
            q: this.q.trim() || undefined,
            industries,
            state: this.state || undefined,
            chapterId: this.chapterId || undefined,
            lookingFor: this.lookingFor ? [this.lookingFor] : [],
            mentor: this.mentorOnly,
            verified: this.verifiedOnly,
            ownerId: this.mineOnly ? this.userId : undefined,
            following: this.followingOnly,
            sort: this.sort,
            page: this.page - 1,
            size: this.pageSize
        };
    }

    private runSearch(): void {
        this.loading = true;
        this.bizService.search(this.params()).pipe(finalize(() => {
            this.loading = false;
            this.loaded = true;
        })).subscribe({
            next: p => {
                this.results = p.items;
                this.total = p.total;
            },
            error: err => {
                console.error('Business search failed', err);
                this.toastr.error('Failed to load businesses.');
            }
        });
    }

    openAdd(): void {
        this.router.navigate(['/biz-directory/new']);
    }
}
